import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar as CalIcon, Clock, ArrowLeft, Gamepad2, Monitor, ShieldCheck, ShoppingCart, Plus, Trash2, User, Phone, Mail, CheckCircle2, Loader2, ArrowRight, Ban, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { generateSlots, fmtTime, fmtDate, toDateStr } from "@/lib/slots";
import { setupIcon } from "@/components/booking/setupIcon";

const STEPS = ["Date", "Platform", "Station", "Duration", "Time", "Setup"];
const emptyDraft = () => ({ date: toDateStr(new Date()), platform: null, station: null, duration: 60, startTime: null });

export default function AdminBooking() {
  const [config, setConfig] = useState(null);
  const [view, setView] = useState("builder"); // builder | cart | details | done
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(emptyDraft());
  const [avail, setAvail] = useState(null);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ name: "", mobile: "", email: "", notes: "" });
  const [method, setMethod] = useState("cash");
  const [payStatus, setPayStatus] = useState("pending");
  const [advance, setAdvance] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [blockOpen, setBlockOpen] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)); }, []);

  const refreshAvail = () => { if (draft.platform && avail) loadAvail(draft.platform, draft.date); };

  const probeSetup = (plat) => config?.setups.find((s) => s.station_type === plat && s.enabled);
  const platformSetups = (plat) => config?.setups.filter((s) => s.station_type === plat && s.enabled) || [];
  const loadAvail = async (plat, d) => { const r = await api.get("/availability", { params: { date: d, setup_id: probeSetup(plat).id } }); setAvail(r.data); };

  const cartIntervals = (dStr, stationId) => cart.filter((c) => c.dateStr === dStr && c.station.id === stationId).map((c) => [c.startMin, c.endMin]);
  const stationSlots = (st, dur) => { if (!avail) return []; const merged = [...st.booked_intervals, ...cartIntervals(draft.date, st.id)]; return generateSlots(avail.open_time, avail.close_time, avail.slot_interval, dur, merged); };
  const stationStatus = (st) => { if (st.status !== "available") return st.status; const merged = [...st.booked_intervals, ...cartIntervals(draft.date, st.id)]; const slots = generateSlots(avail.open_time, avail.close_time, avail.slot_interval, avail.slot_interval, merged); return slots.some((s) => s.available) ? "available" : "booked"; };

  const choosePlatform = async (plat) => { setDraft((d) => ({ ...d, platform: plat, station: null, startTime: null })); await loadAvail(plat, draft.date); setStep(3); };

  const addSetup = (setup) => {
    const [h, m] = draft.startTime.split(":").map(Number);
    const startMin = h * 60 + m; const endMin = startMin + draft.duration;
    setCart((c) => [...c, {
      key: Math.random().toString(36).slice(2), dateStr: draft.date, station: draft.station, setup,
      duration: draft.duration, startTime: draft.startTime, startMin, endMin,
      endTime: `${String(Math.floor(endMin / 60)).padStart(2, "0")}:${String(endMin % 60).padStart(2, "0")}`,
      amount: setup.prices[String(draft.duration)], platform: draft.platform,
      durationLabel: config.durations.find((x) => x.minutes === draft.duration)?.label || `${draft.duration} min`,
    }]);
    setView("cart"); toast.success("Slot added");
  };

  const total = cart.reduce((s, c) => s + c.amount, 0);
  const advVal = payStatus === "paid" ? total : payStatus === "advance" ? Math.max(0, Math.min(total, Number(advance) || 0)) : 0;
  const startBuilder = () => { setDraft(emptyDraft()); setStep(1); setView("builder"); };

  const submit = async () => {
    if (!customer.name.trim() || customer.mobile.trim().length < 10) return toast.error("Name and valid mobile required");
    setLoading(true);
    try {
      const r = await api.post("/admin/bookings/batch", {
        slots: cart.map((c) => ({ date: c.dateStr, setup_id: c.setup.id, station_id: c.station.id, start_time: c.startTime, duration_minutes: c.duration })),
        customer: { customer_name: customer.name, mobile: customer.mobile, email: customer.email, notes: customer.notes, payment_method: method },
        payment_method: method, pay_status: payStatus, advance_amount: advVal,
      });
      setResult(r.data); setView("done");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const reset = () => { setResult(null); setCart([]); setCustomer({ name: "", mobile: "", email: "", notes: "" }); setMethod("cash"); setPayStatus("pending"); setAdvance(""); startBuilder(); };

  if (!config) return <Loader2 className="animate-spin text-purple-400" />;

  if (view === "done" && result) return (
    <div className="glass rounded-3xl p-6 max-w-xl" data-testid="admin-booking-done">
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-3"><CheckCircle2 className="text-emerald-400" size={32} /></div>
      <h2 className="font-display text-xl font-bold text-center">Booking Confirmed</h2>
      <p className="text-center text-sm text-slate-400">Order ID <span className="font-mono text-purple-200">{result.group_id}</span></p>
      <div className="space-y-2 mt-4">
        {result.bookings.map((b) => (
          <div key={b.id} className="glass rounded-xl p-3" data-testid={`admin-confirmed-${b.booking_id}`}>
            <div className="flex justify-between"><span className="font-mono text-purple-300 text-sm">{b.booking_id}</span><span className="font-bold text-emerald-400">₹{b.amount}</span></div>
            <p className="text-sm">{b.station_name} · {b.setup_name}</p>
            <p className="text-xs text-slate-400">{fmtDate(b.date)} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)}</p>
          </div>
        ))}
      </div>
      <div className="glass rounded-xl p-3 mt-3 text-sm flex justify-between"><span className="text-slate-400">Total ₹{result.total} · Paid ₹{result.advance_paid}</span><span className="text-amber-300">Due ₹{result.remaining}</span></div>
      <Button data-testid="admin-book-another" onClick={reset} className="w-full h-11 mt-4 bg-purple-600 hover:bg-purple-500">New Booking</Button>
    </div>
  );

  return (
    <div className="max-w-2xl">
      {view === "builder" && (
        <div className="glass rounded-3xl p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="font-display text-lg font-bold">New Booking</h2>
            <Button data-testid="ab-open-block" onClick={() => setBlockOpen(true)} variant="outline" className="h-9 border-amber-500/40 bg-transparent text-amber-300 hover:bg-amber-500/10">
              <Wrench size={14} /> Block Station
            </Button>
          </div>
          <div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-1">
            {STEPS.map((s, i) => <div key={s} className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-mono uppercase ${step === i + 1 ? "bg-purple-600 text-white" : step > i + 1 ? "text-emerald-400" : "text-slate-500"}`}>{i + 1}. {s}</div>)}
          </div>

          {step === 1 && (
            <Section title="Select Date" icon={CalIcon}>
              <input type="date" data-testid="ab-date" min={toDateStr(new Date())} value={draft.date} onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))} className="h-11 rounded-xl bg-purple-950/30 border border-purple-500/30 px-3 text-sm text-slate-200 [color-scheme:dark]" />
              <Button data-testid="ab-date-next" onClick={() => setStep(2)} className="w-full h-11 mt-5 bg-purple-600 hover:bg-purple-500">Continue <ArrowRight size={15} /></Button>
            </Section>
          )}
          {step === 2 && (
            <Section title="Select Platform" icon={Gamepad2} onBack={() => setStep(1)}>
              <div className="grid grid-cols-2 gap-3">
                {["pc", "ps5"].map((p) => (
                  <button key={p} data-testid={`ab-platform-${p}`} onClick={() => choosePlatform(p)} className="glass rounded-2xl p-6 text-center hover:border-purple-500/60 transition-all">
                    {p === "pc" ? <Monitor className="mx-auto text-purple-300 mb-2" size={28} /> : <Gamepad2 className="mx-auto text-purple-300 mb-2" size={28} />}
                    <h3 className="font-display font-bold">{p === "pc" ? "PC GAMING" : "PS5 CONSOLE"}</h3>
                  </button>
                ))}
              </div>
            </Section>
          )}
          {step === 3 && avail && (
            <Section title="Select Station" icon={ShieldCheck} onBack={() => setStep(2)}>
              <div className="grid sm:grid-cols-2 gap-3">
                {avail.stations.map((st) => { const status = stationStatus(st); const dis = status !== "available"; return (
                  <button key={st.id} data-testid={`ab-station-${st.id}`} disabled={dis} onClick={() => { setDraft((d) => ({ ...d, station: st, startTime: null })); setStep(4); }} className={`text-left glass rounded-2xl p-4 flex justify-between items-center ${dis ? "opacity-50 cursor-not-allowed" : "hover:border-purple-500/60"}`}>
                    <span className="font-semibold">{st.name}</span><span className={`text-[10px] px-2 py-0.5 rounded-full border ${dis ? "text-red-400 border-red-500/30" : "text-emerald-400 border-emerald-500/30"}`}>{dis ? "Booked" : "Available"}</span>
                  </button>); })}
              </div>
            </Section>
          )}
          {step === 4 && (
            <Section title="Select Duration" icon={Clock} onBack={() => setStep(3)}>
              <div className="flex flex-wrap gap-3">
                {config.durations.map((d) => <button key={d.minutes} data-testid={`ab-duration-${d.minutes}`} onClick={() => { setDraft((x) => ({ ...x, duration: d.minutes, startTime: null })); setStep(5); }} className={`px-5 h-12 rounded-xl border font-semibold ${draft.duration === d.minutes ? "bg-purple-600 border-purple-400" : "glass text-slate-300"}`}>{d.label}</button>)}
              </div>
            </Section>
          )}
          {step === 5 && (
            <Section title="Select Start Time" icon={Clock} onBack={() => setStep(4)}>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                {stationSlots(draft.station, draft.duration).map((s) => <button key={s.start} data-testid={`ab-slot-${s.start}`} disabled={!s.available} onClick={() => { setDraft((x) => ({ ...x, startTime: s.start })); setStep(6); }} className={`h-11 rounded-xl border text-sm font-mono ${!s.available ? "opacity-30 line-through cursor-not-allowed" : "glass text-slate-200 hover:border-purple-500/50"}`}>{fmtTime(s.start)}</button>)}
              </div>
            </Section>
          )}
          {step === 6 && (
            <Section title="Select Setup" icon={Gamepad2} onBack={() => setStep(5)}>
              <div className="grid sm:grid-cols-2 gap-3">
                {platformSetups(draft.platform).map((s) => { const Icon = setupIcon(s.id); return (
                  <button key={s.id} data-testid={`ab-setup-${s.id}`} onClick={() => addSetup(s)} className="text-left glass rounded-2xl p-4 hover:border-purple-500/60">
                    <div className="flex items-center gap-2 mb-1"><Icon className="text-purple-300" size={18} /><h3 className="font-bold">{s.name}</h3></div>
                    <span className="text-sm text-emerald-400 font-mono">₹{s.prices[String(draft.duration)]}</span>
                  </button>); })}
              </div>
            </Section>
          )}
        </div>
      )}

      {view === "cart" && (
        <Section title={`Cart · ${cart.length} slot(s)`} icon={ShoppingCart} card>
          {cart.length === 0 ? <p className="text-slate-500 text-center py-6">Cart empty.</p> : (
            <div className="space-y-2">
              {cart.map((c, i) => (
                <div key={c.key} className="glass rounded-xl p-3 flex justify-between items-start" data-testid={`ab-cart-${i}`}>
                  <div><p className="font-semibold">{c.station.name} · {c.setup.name}</p><p className="text-xs text-slate-400">{fmtDate(c.dateStr)} · {fmtTime(c.startTime)}–{fmtTime(c.endTime)} · {c.durationLabel}</p></div>
                  <div className="text-right"><p className="font-bold text-emerald-400">₹{c.amount}</p><button data-testid={`ab-remove-${i}`} onClick={() => setCart((x) => x.filter((y) => y.key !== c.key))} className="text-red-300 text-xs flex items-center gap-1 ml-auto"><Trash2 size={12} /> Remove</button></div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center glass rounded-xl p-3 mt-3"><span className="text-slate-400 text-sm uppercase">Total</span><span className="font-display text-xl font-bold text-emerald-400" data-testid="ab-total">₹{total}</span></div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" data-testid="ab-add-more" onClick={startBuilder} className="h-11 border-purple-500/30 bg-transparent"><Plus size={15} /> Add Slot</Button>
            <Button data-testid="ab-to-details" disabled={!cart.length} onClick={() => setView("details")} className="h-11 flex-1 bg-purple-600 hover:bg-purple-500">Customer & Payment <ArrowRight size={15} /></Button>
          </div>
        </Section>
      )}

      {view === "details" && (
        <Section title="Customer & Payment" icon={User} card onBack={() => setView("cart")}>
          <div className="space-y-3">
            <Field icon={User} label="Full Name" testid="ab-name" value={customer.name} onChange={(v) => setCustomer({ ...customer, name: v })} />
            <Field icon={Phone} label="Mobile (WhatsApp)" testid="ab-mobile" value={customer.mobile} onChange={(v) => setCustomer({ ...customer, mobile: v.replace(/[^0-9+]/g, "") })} />
            <Field icon={Mail} label="Email (optional)" testid="ab-email" value={customer.email} onChange={(v) => setCustomer({ ...customer, email: v })} />
            <div>
              <label className="text-xs uppercase text-purple-400 font-mono">Payment Method</label>
              <div className="flex gap-2 mt-1">{["cash", "upi", "payu"].map((m) => <button key={m} data-testid={`ab-method-${m}`} onClick={() => setMethod(m)} className={`flex-1 h-10 rounded-xl text-sm border uppercase ${method === m ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>{m}</button>)}</div>
            </div>
            <div>
              <label className="text-xs uppercase text-purple-400 font-mono">Payment Status</label>
              <div className="flex gap-2 mt-1">{[["pending", "Pay at Café"], ["advance", "Advance"], ["paid", "Paid Full"]].map(([v, l]) => <button key={v} data-testid={`ab-paystatus-${v}`} onClick={() => setPayStatus(v)} className={`flex-1 h-10 rounded-xl text-sm border ${payStatus === v ? "bg-purple-600 border-purple-400" : "glass border-purple-500/30 text-slate-300"}`}>{l}</button>)}</div>
            </div>
            {payStatus === "advance" && <Input data-testid="ab-advance" type="number" value={advance} onChange={(e) => setAdvance(e.target.value)} placeholder={`Advance amount (max ₹${total})`} className="h-11 bg-purple-950/30 border-purple-500/30" />}
            <div className="glass rounded-xl p-3 text-sm flex justify-between"><span className="text-slate-400">Total ₹{total} · Paying ₹{advVal}</span><span className="text-amber-300">Due ₹{total - advVal}</span></div>
            <Button data-testid="ab-submit" disabled={loading} onClick={submit} className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 font-bold">{loading ? <Loader2 className="animate-spin" /> : "Confirm Booking (No OTP)"}</Button>
          </div>
        </Section>
      )}

      <BlockDialog open={blockOpen} onOpenChange={setBlockOpen} config={config} defaultDate={draft.date} onChanged={refreshAvail} />
    </div>
  );
}

function Section({ title, icon: Icon, children, onBack, card }) {
  const inner = (
    <>
      <div className="flex items-center gap-3 mb-5">
        {onBack && <button onClick={onBack} className="text-slate-400 hover:text-white"><ArrowLeft size={18} /></button>}
        <div className="w-10 h-10 rounded-xl bg-purple-600/20 border border-purple-500/40 flex items-center justify-center"><Icon className="text-purple-300" size={20} /></div>
        <h2 className="font-display text-lg font-bold">{title}</h2>
      </div>
      {children}
    </>
  );
  return card ? <div className="glass rounded-3xl p-5">{inner}</div> : inner;
}

function Field({ icon: Icon, label, testid, value, onChange }) {
  return (
    <div>
      <label className="text-xs uppercase text-purple-400 font-mono">{label}</label>
      <div className="relative mt-1"><Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} /><Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="pl-9 h-11 bg-purple-950/30 border-purple-500/30" /></div>
    </div>
  );
}

function BlockDialog({ open, onOpenChange, config, defaultDate, onChanged }) {
  const [stationId, setStationId] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [wholeDay, setWholeDay] = useState(true);
  const [start, setStart] = useState(config?.open_time || "10:00");
  const [end, setEnd] = useState(config?.close_time || "23:00");
  const [reason, setReason] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);

  const stations = config?.stations || [];
  const stName = (id) => stations.find((s) => s.id === id)?.name || id;

  const loadBlocks = () => api.get("/admin/blocks").then((r) => setBlocks(r.data || []));

  useEffect(() => {
    if (!open) return;
    setStationId((p) => p || stations[0]?.id || "");
    setDate(defaultDate);
    setWholeDay(true);
    setStart(config?.open_time || "10:00");
    setEnd(config?.close_time || "23:00");
    setReason("");
    loadBlocks();
  }, [open]); // eslint-disable-line

  const addBlock = async () => {
    if (!stationId || !date) return toast.error("Station and date required");
    const s = wholeDay ? (config?.open_time || "10:00") : start;
    const e = wholeDay ? (config?.close_time || "23:00") : end;
    if (s >= e) return toast.error("End time must be after start time");
    setSaving(true);
    try {
      await api.post("/admin/blocks", { station_id: stationId, date, start_time: s, end_time: e, reason: reason || "Maintenance" });
      toast.success("Station blocked");
      setReason("");
      await loadBlocks();
      onChanged?.();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const unblock = async (id) => {
    try { await api.delete(`/admin/blocks/${id}`); toast.success("Unblocked"); await loadBlocks(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const sorted = [...blocks].sort((a, b) => (a.date + a.start_time).localeCompare(b.date + b.start_time));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-amber-500/30 max-w-lg" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Wrench className="text-amber-300" size={18} /> Block Station (Maintenance)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase text-amber-300/80 font-mono">Station</label>
            <select data-testid="block-station" value={stationId} onChange={(e) => setStationId(e.target.value)} className="mt-1 w-full h-11 rounded-xl bg-purple-950/40 border border-purple-500/30 px-3 text-sm text-slate-200">
              {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase text-amber-300/80 font-mono">Date</label>
            <Input type="date" data-testid="block-date" min={toDateStr(new Date())} value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-11 bg-purple-950/30 border-purple-500/30 [color-scheme:dark]" />
          </div>
          <div className="flex items-center justify-between glass rounded-xl px-3 py-2.5">
            <span className="text-sm text-slate-200">Block whole day</span>
            <Switch data-testid="block-wholeday" checked={wholeDay} onCheckedChange={setWholeDay} />
          </div>
          {!wholeDay && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs uppercase text-amber-300/80 font-mono">Start</label><Input type="time" data-testid="block-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-11 bg-purple-950/30 border-purple-500/30 [color-scheme:dark]" /></div>
              <div><label className="text-xs uppercase text-amber-300/80 font-mono">End</label><Input type="time" data-testid="block-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-11 bg-purple-950/30 border-purple-500/30 [color-scheme:dark]" /></div>
            </div>
          )}
          <Input data-testid="block-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional, default: Maintenance)" className="h-11 bg-purple-950/30 border-purple-500/30" />
          <Button data-testid="block-submit" disabled={saving || !date} onClick={addBlock} className="w-full h-11 bg-amber-600 hover:bg-amber-500 font-bold text-white">{saving ? <Loader2 className="animate-spin" /> : <><Ban size={15} /> Block Station</>}</Button>

          <div className="pt-2">
            <p className="text-xs uppercase text-slate-400 font-mono mb-2">Existing Blocks</p>
            {sorted.length === 0 ? <p className="text-slate-500 text-sm text-center py-3" data-testid="block-empty">No active blocks.</p> : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {sorted.map((b) => (
                  <div key={b.id} className="flex justify-between items-center text-sm glass rounded-xl px-3 py-2" data-testid={`block-row-${b.id}`}>
                    <span className="text-slate-200">{stName(b.station_id)} · {fmtDate(b.date)} · {fmtTime(b.start_time)}–{fmtTime(b.end_time)}{b.reason ? ` · ${b.reason}` : ""}</span>
                    <Button size="icon" variant="outline" data-testid={`block-unblock-${b.id}`} onClick={() => unblock(b.id)} className="h-8 w-8 shrink-0 border-red-500/30 bg-transparent text-red-300"><Trash2 size={13} /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
